"use client";

import {
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import Image from "next/image";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useMediaQuery } from "@react-hook/media-query";
import { AnimatePresence, motion } from "framer-motion";
import { Download, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";

import logo from "@/assets/img/logo/logo.png";
import { useMotionControl } from "@/lib/MotionProvider";
import "@/styles/glassmorphism.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import styles from "./Flipbook.module.css";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

type FlipDirection = "forward" | "backward";

interface FlipbookProps {
  src: string;
  title: string;
  initialPage?: number;
}

type SearchParamsLike = Pick<URLSearchParams, "get" | "toString">;

const MAX_PAGE_WIDTH = 800;
const MIN_ZOOM = 0.75;
const MAX_ZOOM = 2.25;
const ZOOM_STEP = 0.15;

function getInitialViewportWidth() {
  if (typeof window === "undefined") {
    return MAX_PAGE_WIDTH;
  }

  return window.innerWidth;
}

function parsePageParam(
  searchParams: SearchParamsLike | null,
  fallback: number
) {
  const rawPage = searchParams?.get("page");
  if (!rawPage) return fallback;

  const parsedPage = Number.parseInt(rawPage, 10);
  if (!Number.isFinite(parsedPage) || parsedPage < 1) return fallback;

  return parsedPage;
}

function clampPage(nextPage: number, totalPages: number) {
  if (totalPages < 1) {
    return Math.max(1, nextPage);
  }

  return Math.min(Math.max(nextPage, 1), totalPages);
}

function getDownloadFileName(src: string, title: string) {
  const normalizedSrc = src.split("#")[0]?.split("?")[0] ?? "";
  const fileName = normalizedSrc.split("/").pop();

  if (fileName && fileName.toLowerCase().endsWith(".pdf")) {
    return fileName;
  }

  const sanitizedTitle = title.trim().replace(/\s+/g, "-");

  return sanitizedTitle ? `${sanitizedTitle}.pdf` : "document.pdf";
}

export default function Flipbook({
  src,
  title,
  initialPage = 1,
}: FlipbookProps) {
  const { motionAllowed } = useMotionControl();
  const isDesktop = useMediaQuery("(min-width: 992px)");
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();

  const toolbarRef = useRef<HTMLElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const stageScrollerRef = useRef<HTMLDivElement | null>(null);
  const activeThumbnailRef = useRef<HTMLButtonElement | null>(null);

  const [toolbarHeight, setToolbarHeight] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(getInitialViewportWidth);
  const [stageViewportWidth, setStageViewportWidth] = useState<number | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(initialPage);
  const [direction, setDirection] = useState<FlipDirection>("forward");
  const [zoom, setZoom] = useState(1);
  const [thumbnailsOpen, setThumbnailsOpen] = useState(true);

  useEffect(() => {
    const syncViewportWidth = () => {
      setViewportWidth(window.innerWidth);
    };

    syncViewportWidth();
    window.addEventListener("resize", syncViewportWidth);

    return () => {
      window.removeEventListener("resize", syncViewportWidth);
    };
  }, []);

  useEffect(() => {
    const toolbarElement = toolbarRef.current;
    if (!toolbarElement) return;

    const syncToolbarHeight = () => {
      setToolbarHeight(Math.ceil(toolbarElement.getBoundingClientRect().height));
    };

    syncToolbarHeight();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", syncToolbarHeight);

      return () => {
        window.removeEventListener("resize", syncToolbarHeight);
      };
    }

    const resizeObserver = new ResizeObserver(() => {
      syncToolbarHeight();
    });

    resizeObserver.observe(toolbarElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const stageScrollerElement = stageScrollerRef.current;
    if (!stageScrollerElement) return;

    const syncStageViewportWidth = () => {
      const nextWidth = Math.floor(stageScrollerElement.getBoundingClientRect().width);
      if (nextWidth < 1) return;

      setStageViewportWidth(nextWidth);
    };

    syncStageViewportWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", syncStageViewportWidth);

      return () => {
        window.removeEventListener("resize", syncStageViewportWidth);
      };
    }

    const resizeObserver = new ResizeObserver(() => {
      syncStageViewportWidth();
    });

    resizeObserver.observe(stageScrollerElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isDesktop, thumbnailsOpen]);

  useEffect(() => {
    const requestedPage = clampPage(
      parsePageParam(searchParams, initialPage),
      numPages
    );

    if (requestedPage === page) return;

    setDirection(requestedPage > page ? "forward" : "backward");
    startTransition(() => {
      setPage(requestedPage);
    });
  }, [initialPage, numPages, page, searchParams]);

  useEffect(() => {
    activeThumbnailRef.current?.scrollIntoView({
      block: "nearest",
      behavior: "smooth",
    });
  }, [page, isDesktop, thumbnailsOpen]);

  const replacePageInUrl = useCallback((nextPage: number) => {
    if (!pathname) return;

    const nextSearchParams = new URLSearchParams(searchParams?.toString());

    if (nextPage <= 1) {
      nextSearchParams.delete("page");
    } else {
      nextSearchParams.set("page", String(nextPage));
    }

    const nextQuery = nextSearchParams.toString();
    const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;

    router.replace(nextUrl, { scroll: false });
  }, [pathname, router, searchParams]);

  const goToPage = useCallback((nextPage: number) => {
    const clampedPage = clampPage(nextPage, numPages);
    if (clampedPage === page) return;

    setDirection(clampedPage > page ? "forward" : "backward");
    startTransition(() => {
      setPage(clampedPage);
    });
    replacePageInUrl(clampedPage);
    stageRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [numPages, page, replacePageInUrl]);

  const changeZoom = useCallback((type: "in" | "out") => {
    setZoom((currentZoom) => {
      const nextZoom =
        type === "in" ? currentZoom + ZOOM_STEP : currentZoom - ZOOM_STEP;

      return Math.min(
        MAX_ZOOM,
        Math.max(MIN_ZOOM, Number(nextZoom.toFixed(2)))
      );
    });
  }, []);

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName;

      if (tagName === "INPUT" || tagName === "TEXTAREA") {
        return;
      }

      if (event.key === "ArrowRight" || event.key === "PageDown") {
        event.preventDefault();
        goToPage(page + 1);
      }

      if (event.key === "ArrowLeft" || event.key === "PageUp") {
        event.preventDefault();
        goToPage(page - 1);
      }

      if ((event.ctrlKey || event.metaKey) && (event.key === "+" || event.key === "=")) {
        event.preventDefault();
        changeZoom("in");
      }

      if ((event.ctrlKey || event.metaKey) && event.key === "-") {
        event.preventDefault();
        changeZoom("out");
      }

      if ((event.ctrlKey || event.metaKey) && event.key === "0") {
        event.preventDefault();
        setZoom(1);
      }
    };

    window.addEventListener("keydown", handleKeydown);

    return () => {
      window.removeEventListener("keydown", handleKeydown);
    };
  }, [changeZoom, goToPage, page]);

  useEffect(() => {
    const stageElement = stageRef.current;
    if (!stageElement) return;

    let startX = 0;
    let startY = 0;

    const handleTouchStart = (event: TouchEvent) => {
      startX = event.touches[0]?.clientX ?? 0;
      startY = event.touches[0]?.clientY ?? 0;
    };

    const handleTouchEnd = (event: TouchEvent) => {
      const endX = event.changedTouches[0]?.clientX ?? 0;
      const endY = event.changedTouches[0]?.clientY ?? 0;
      const deltaX = endX - startX;
      const deltaY = endY - startY;

      if (Math.abs(deltaX) <= Math.abs(deltaY) || Math.abs(deltaX) < 48) {
        return;
      }

      if (deltaX < 0) {
        goToPage(page + 1);
        return;
      }

      goToPage(page - 1);
    };

    stageElement.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    stageElement.addEventListener("touchend", handleTouchEnd, {
      passive: true,
    });

    return () => {
      stageElement.removeEventListener("touchstart", handleTouchStart);
      stageElement.removeEventListener("touchend", handleTouchEnd);
    };
  }, [goToPage, page]);

  const fittedViewportWidth = Math.floor(viewportWidth * 0.95);
  const fittedStageWidth = stageViewportWidth ?? fittedViewportWidth;
  const pageWidth = Math.max(
    1,
    Math.min(MAX_PAGE_WIDTH, fittedViewportWidth, fittedStageWidth)
  );
  const renderWidth = Math.max(1, Math.round(pageWidth * zoom));
  const canGoPrevious = page > 1;
  const canGoNext = numPages > 0 && page < numPages;
  const pageNumbers = Array.from({ length: numPages }, (_, index) => index + 1);
  const preloadPages = pageNumbers.filter(
    (pageNumber) => pageNumber === page - 1 || pageNumber === page + 1
  );
  const showThumbnailDrawer = isDesktop && thumbnailsOpen && numPages > 0;
  const downloadFileName = getDownloadFileName(src, title);

  const currentPageLabel =
    numPages > 0 ? `Page ${page} of ${numPages}` : "Loading document";
  const zoomLabel = `${Math.round(zoom * 100)}%`;
  const shellStyle = {
    "--flipbook-toolbar-offset": `${toolbarHeight > 0 ? toolbarHeight + 32 : 176}px`,
  } as CSSProperties;

  const currentPageNode = (
    <Page
      pageNumber={page}
      width={renderWidth}
      renderAnnotationLayer={false}
      renderTextLayer={false}
      loading={<div className={styles.pageState}>Loading page…</div>}
      error={<div className={styles.pageState}>Unable to render this page.</div>}
    />
  );

  return (
    <div className={`app-shell ${styles.shell}`} style={shellStyle}>
      <div className={styles.frame}>
        <header ref={toolbarRef} className={`glass-card ${styles.toolbar}`}>
          <div className={styles.brandBlock}>
            <div className={styles.logoWrap}>
              <Image
                src={logo}
                alt="Invtron"
                className={styles.logo}
                priority
              />
            </div>

            <div className={styles.titleBlock}>
              <span className={styles.eyebrow}>Invtron Documents</span>
              <h1 className={styles.title}>{title}</h1>
              <p className={styles.subtitle}>{currentPageLabel}</p>
            </div>
          </div>

          <div className={styles.controls}>
            {isDesktop ? (
              <button
                type="button"
                className={`flip-button ${styles.toolbarButton}`}
                onClick={() => setThumbnailsOpen((current) => !current)}
                aria-pressed={thumbnailsOpen}
              >
                {thumbnailsOpen ? (
                  <PanelLeftClose aria-hidden="true" />
                ) : (
                  <PanelLeftOpen aria-hidden="true" />
                )}
                <span>Pages</span>
              </button>
            ) : null}

            <button
              type="button"
              className={`flip-button ${styles.toolbarButton}`}
              onClick={() => goToPage(page - 1)}
              disabled={!canGoPrevious}
            >
              {"< Previous"}
            </button>

            <div className={styles.sliderCluster}>
              <input
                type="range"
                min={1}
                max={Math.max(numPages, 1)}
                step={1}
                value={page}
                onChange={(event) =>
                  goToPage(Number.parseInt(event.target.value, 10))
                }
                className={styles.slider}
                aria-label="Document page slider"
                disabled={numPages < 1}
              />

              <div className={styles.sliderMeta}>
                <span>{currentPageLabel}</span>
                <span>{zoomLabel}</span>
              </div>
            </div>

            <button
              type="button"
              className={`flip-button ${styles.toolbarButton}`}
              onClick={() => goToPage(page + 1)}
              disabled={!canGoNext}
            >
              {"Next >"}
            </button>

            <div className={styles.zoomControls}>
              <button
                type="button"
                className={`flip-button ${styles.toolbarButton}`}
                onClick={() => changeZoom("out")}
                disabled={zoom <= MIN_ZOOM}
                aria-label="Zoom out"
              >
                -
              </button>

              <button
                type="button"
                className={`flip-button ${styles.toolbarButton}`}
                onClick={() => changeZoom("in")}
                disabled={zoom >= MAX_ZOOM}
                aria-label="Zoom in"
              >
                +
              </button>

              <button
                type="button"
                className={`flip-button ${styles.toolbarButton}`}
                onClick={() => setZoom(1)}
                disabled={zoom === 1}
              >
                Reset Size
              </button>
            </div>

            <a
              href={src}
              download={downloadFileName}
              className={`flip-button ${styles.toolbarButton} ${styles.downloadButton}`}
              aria-label={`Download ${title} PDF`}
            >
              <Download aria-hidden="true" />
              <span>Download PDF</span>
            </a>
          </div>
        </header>

        <Document
          file={src}
          onLoadSuccess={({ numPages: loadedPages }) => {
            setNumPages(loadedPages);

            const requestedPage = clampPage(
              parsePageParam(searchParams, initialPage),
              loadedPages
            );

            if (requestedPage === page) return;

            setDirection(requestedPage > page ? "forward" : "backward");
            startTransition(() => {
              setPage(requestedPage);
            });
          }}
          loading={
            <div className={`glass-page ${styles.documentState}`}>
              Loading document…
            </div>
          }
          error={
            <div className={`glass-page ${styles.documentState}`}>
              Failed to load this PDF.
            </div>
          }
        >
          <div className={styles.workspace}>
            {showThumbnailDrawer ? (
              <aside className={`glass-card ${styles.drawer}`}>
                <div className={styles.drawerHeader}>
                  <span>Pages</span>
                  <span>{numPages}</span>
                </div>

                <div className={styles.drawerList}>
                  {pageNumbers.map((pageNumber) => (
                    <button
                      key={pageNumber}
                      type="button"
                      onClick={() => goToPage(pageNumber)}
                      aria-current={pageNumber === page ? "page" : undefined}
                      className={`${styles.thumbnailButton} ${
                        pageNumber === page ? styles.thumbnailButtonActive : ""
                      }`}
                      ref={pageNumber === page ? activeThumbnailRef : null}
                    >
                      <span className={styles.thumbnailLabel}>
                        Page {pageNumber}
                      </span>

                      <div className={styles.thumbnailCanvas}>
                        <Page
                          pageNumber={pageNumber}
                          width={150}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                          loading={<div className={styles.thumbnailState} />}
                        />
                      </div>
                    </button>
                  ))}
                </div>
              </aside>
            ) : null}

            <section className={styles.viewerColumn}>
              <div className={`glass-page ${styles.viewerCard}`}>
                <div className={styles.viewerHeader}>
                  <span>{title}</span>
                  <span>{currentPageLabel}</span>
                </div>

                <div ref={stageRef} className={styles.stage}>
                  <button
                    type="button"
                    className={`${styles.hotspot} ${styles.hotspotLeft}`}
                    onClick={() => goToPage(page - 1)}
                    disabled={!canGoPrevious}
                    aria-label="Previous page"
                  />

                  <button
                    type="button"
                    className={`${styles.hotspot} ${styles.hotspotRight}`}
                    onClick={() => goToPage(page + 1)}
                    disabled={!canGoNext}
                    aria-label="Next page"
                  />

                  <div ref={stageScrollerRef} className={styles.stageScroller}>
                    <div
                      className={styles.pageSurface}
                      style={{ width: renderWidth }}
                    >
                      {motionAllowed ? (
                        <AnimatePresence mode="wait" initial={false}>
                          <motion.div
                            key={page}
                            className={styles.pageMotion}
                            initial={{
                              opacity: 0,
                              x: direction === "forward" ? 28 : -28,
                              rotateY: direction === "forward" ? -12 : 12,
                            }}
                            animate={{ opacity: 1, x: 0, rotateY: 0 }}
                            exit={{
                              opacity: 0,
                              x: direction === "forward" ? -28 : 28,
                              rotateY: direction === "forward" ? 12 : -12,
                            }}
                            transition={{ duration: 0.28, ease: "easeInOut" }}
                          >
                            {currentPageNode}
                          </motion.div>
                        </AnimatePresence>
                      ) : (
                        currentPageNode
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {!isDesktop ? (
                <div className={styles.mobilePagerWrap}>
                  <div className={`glass-card ${styles.mobilePager}`}>
                    <button
                      type="button"
                      className={`flip-button ${styles.toolbarButton}`}
                      onClick={() => goToPage(page - 1)}
                      disabled={!canGoPrevious}
                    >
                      {"< Previous"}
                    </button>

                    <div className={styles.mobilePagerCount}>
                      {page} / {numPages || "—"}
                    </div>

                    <button
                      type="button"
                      className={`flip-button ${styles.toolbarButton}`}
                      onClick={() => goToPage(page + 1)}
                      disabled={!canGoNext}
                    >
                      {"Next >"}
                    </button>
                  </div>
                </div>
              ) : null}
            </section>

            <div aria-hidden="true" className={styles.preloadBuffer}>
              {preloadPages.map((pageNumber) => (
                <Page
                  key={`preload-${pageNumber}-${renderWidth}`}
                  pageNumber={pageNumber}
                  width={renderWidth}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                />
              ))}
            </div>
          </div>
        </Document>
      </div>
    </div>
  );
}
